import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { NoteDto, NotesService } from '../../../services/axios/axios-api-v2-generated';
import { Note, NoteCategory } from '../../../types/notes';
import dayjs from 'dayjs';
import { addNotification, NotificationType } from './global-slice';
import { logger } from '@fe/core';
import { decodeHtmlContent, encodeHtmlContent } from '../../../services/html-service';

type NotesSlice = {
    busy: boolean;
    notes: Record<string, Note[]>;
    lastFetchTime: Record<string, number>;
    aircraftWithNotes: string[];
    airportsWithNotes: string[];
};

const initialState: NotesSlice = {
    busy: false,
    notes: {},
    lastFetchTime: {},
    aircraftWithNotes: [],
    airportsWithNotes: []
};

const CACHE_DURATION = 100 * 1000; // 100 seconds

const toNote = (dto: NoteDto): Note => ({
    id: dto.id!,
    itemId: dto.itemId!,
    content: decodeHtmlContent(dto.content ?? ''),
    origin: dto.origin,
    departureDateTime: dto.depDateTime ? dayjs(dto.depDateTime, { utc: true }).unix() : undefined,
    eta: dto.etaDate ? dayjs(dto.etaDate, { utc: true }).unix() : undefined,
    dateCreated: dayjs(dto.dateCreated, { utc: true }).unix(),
    createdBy: dto.createdBy!,
    editedBy: dto.editedBy,
    dateEdited: dto.dateEdited ? dayjs(dto.dateEdited, { utc: true }).unix() : undefined,
    isCompany: dto.isCompany ?? false,
    category: dto.type as NoteCategory
});

export const notesSlice = createSlice({
    name: 'notes',
    initialState: initialState,
    reducers: {
        setNotesSummary(state, params: { payload: { aircraftWithNotes: string[]; airportsWithNotes: string[] } }) {
            state.aircraftWithNotes = params.payload.aircraftWithNotes;
            state.airportsWithNotes = params.payload.airportsWithNotes;
        },
        invalidateCache(state, params: { payload: { itemId: string } }) {
            delete state.lastFetchTime[params.payload.itemId];
        }
    },
    extraReducers: (builder) => {
        // Fetch notes for item
        builder.addCase(fetchNotesForItem.pending, (state) => {
            state.busy = true;
        });
        builder.addCase(fetchNotesForItem.fulfilled, (state, action) => {
            state.busy = false;
            const { itemId, notes } = action.payload;
            state.notes[itemId] = notes;
            state.lastFetchTime[itemId] = Date.now();
        });
        builder.addCase(fetchNotesForItem.rejected, (state) => {
            state.busy = false;
        });

        // Fetch notes for items
        builder.addCase(fetchNotesForItems.pending, (state) => {
            state.busy = true;
        });
        builder.addCase(fetchNotesForItems.fulfilled, (state, action) => {
            state.busy = false;
            const notesByItemId = action.payload;
            for (const itemId in notesByItemId) {
                state.notes[itemId] = notesByItemId[itemId];
                state.lastFetchTime[itemId] = Date.now();
            }
        });
        builder.addCase(fetchNotesForItems.rejected, (state) => {
            state.busy = false;
        });

        // Create note
        builder.addCase(createNote.pending, (state) => {
            state.busy = true;
        });
        builder.addCase(createNote.fulfilled, (state, action) => {
            state.busy = false;
            const note = action.payload;
            state.notes[note.itemId] ??= [];
            state.notes[note.itemId].push(note);
            delete state.lastFetchTime[note.itemId];

            const list = note.category === NoteCategory.aircraft ? state.aircraftWithNotes : state.airportsWithNotes;
            if (!list.includes(note.itemId)) {
                list.push(note.itemId);
            }
            if (note.category === NoteCategory.aircraft) {
                state.aircraftWithNotes = list;
            } else {
                state.airportsWithNotes = list;
            }
        });
        builder.addCase(createNote.rejected, (state) => {
            state.busy = false;
        });

        // Update note
        builder.addCase(updateNote.pending, (state) => {
            state.busy = true;
        });
        builder.addCase(updateNote.fulfilled, (state, action) => {
            state.busy = false;
            const note = action.payload;
            const notes = state.notes[note.itemId];
            if (notes) {
                const noteIndex = notes.findIndex((n) => n.id === note.id);
                if (noteIndex > -1) {
                    state.notes[note.itemId][noteIndex] = note;
                }
            }
            delete state.lastFetchTime[note.itemId];
        });
        builder.addCase(updateNote.rejected, (state) => {
            state.busy = false;
        });

        // Delete note
        builder.addCase(deleteNote.pending, (state) => {
            state.busy = true;
        });
        builder.addCase(deleteNote.fulfilled, (state, action) => {
            state.busy = false;
            const { itemId, noteId, category } = action.payload;
            const notes = state.notes[itemId];
            if (notes) {
                state.notes[itemId] = notes.filter((n) => n.id !== noteId);
                delete state.lastFetchTime[itemId];

                if (state.notes[itemId].length === 0) {
                    const list = category === NoteCategory.aircraft ? state.aircraftWithNotes : state.airportsWithNotes;
                    const index = list.indexOf(itemId);
                    if (index > -1) {
                        list.splice(index, 1);
                    }
                }
            }
        });
        builder.addCase(deleteNote.rejected, (state) => {
            state.busy = false;
        });
    }
});

export const fetchNotesForItem = createAsyncThunk(
    'notes/fetchNotesForItem',
    async (params: { itemId: string; force?: boolean }, thunkAPI) => {
        const state = thunkAPI.getState() as { notes: NotesSlice };
        const lastFetch = state.notes.lastFetchTime[params.itemId];
        const isFresh = lastFetch && Date.now() - lastFetch < CACHE_DURATION;

        if (isFresh && !params.force) {
            return { itemId: params.itemId, notes: state.notes.notes[params.itemId] ?? [] };
        }

        try {
            const result = await NotesService.getNotes({ itemIds: [params.itemId] }, { withAuthorization: true });
            const notes = result.items?.map(toNote) ?? [];
            return { itemId: params.itemId, notes };
        } catch (error) {
            logger.error('Error fetching notes', error);
            thunkAPI.dispatch(addNotification({ type: NotificationType.generalError, customMessage: 'Error fetching notes' }));
            return thunkAPI.rejectWithValue(undefined);
        }
    }
);

export const fetchNotesForItems = createAsyncThunk('notes/fetchNotesForItems', async (params: { itemIds: string[] }, thunkAPI) => {
    try {
        const result = await NotesService.getNotes(
            {
                itemIds: params.itemIds
            },
            { withAuthorization: true }
        );
        const notes = result.items?.map(toNote) ?? [];

        const notesByItemId: Record<string, Note[]> = {};
        for (const note of notes) {
            if (!notesByItemId[note.itemId]) {
                notesByItemId[note.itemId] = [];
            }
            notesByItemId[note.itemId].push(note);
        }

        return notesByItemId;
    } catch (error) {
        logger.error('Error fetching notes for items', error);
        thunkAPI.dispatch(addNotification({ type: NotificationType.generalError, customMessage: 'Error fetching notes for items' }));
        return thunkAPI.rejectWithValue(undefined);
    }
});

export const createNote = createAsyncThunk('notes/createNote', async (params: { note: Omit<Note, 'id'> }, thunkAPI) => {
    try {
        const result = await NotesService.createNote(
            {
                body: {
                    type: params.note.category,
                    itemId: params.note.itemId,
                    content: encodeHtmlContent(params.note.content),
                    origin: params.note.origin ?? undefined,
                    depDateTime: params.note.departureDateTime ? dayjs(params.note.departureDateTime).toISOString() : undefined,
                    etaDate: params.note.eta ? dayjs(params.note.eta).toISOString() : undefined,
                    isCompany: params.note.isCompany
                }
            },
            { withAuthorization: true }
        );
        return toNote(result as NoteDto);
    } catch (error) {
        logger.error('Error creating note', error);
        thunkAPI.dispatch(addNotification({ type: NotificationType.generalError, customMessage: 'Failed to create note' }));
        return thunkAPI.rejectWithValue(undefined);
    }
});

export const updateNote = createAsyncThunk('notes/updateNote', async (params: { note: Note }, thunkAPI) => {
    try {
        const result = await NotesService.updateNote(
            {
                id: params.note.id,
                body: {
                    content: encodeHtmlContent(params.note.content)
                }
            },
            { withAuthorization: true }
        );
        return toNote(result);
    } catch (error) {
        logger.error('Error updating note', error);
        thunkAPI.dispatch(addNotification({ type: NotificationType.generalError, customMessage: 'Failed to update the note' }));
        return thunkAPI.rejectWithValue(undefined);
    }
});

export const deleteNote = createAsyncThunk('notes/deleteNote', async (params: { note: Note }, thunkAPI) => {
    try {
        await NotesService.deleteNote({ id: params.note.id }, { withAuthorization: true });
        return {
            itemId: params.note.itemId,
            noteId: params.note.id,
            category: params.note.category
        };
    } catch (error) {
        logger.error('Error deleting note', error);
        thunkAPI.dispatch(addNotification({ type: NotificationType.generalError, customMessage: 'Failed to delete the note' }));
        return thunkAPI.rejectWithValue(undefined);
    }
});

export const { setNotesSummary, invalidateCache } = notesSlice.actions;
